import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
// Images
import floodwiseImg from "../assets/images/floodwise.png";
import medicalImg from "../assets/images/medicalimages.png";
import pharmaImg from "../assets/images/pharmaapp.png";
import livenessImg from "../assets/images/liveness.png";
import businessImg from "../assets/images/bizmatch.png";
import crowdImg from "../assets/images/headcount.png";

interface Project {
  title: string;
  category: string;
  description: string;
  image: string;
  link: string;
  year: string;
}

const projects: Project[] = [
  {
    title: "FloodWise",
    category: "IoT System",
    description: "Real-time flood detection dashboard via MQTT.",
    image: floodwiseImg,
    link: "https://github.com/raaviian/flood-wise.git",
    year: "2024"
  },
  {
    title: "Medical Image Enhancer Using AI",
    category: "Computer Vision",
    description: "Enhancing medical scans using AI.",
    image: medicalImg,
    link: "https://github.com/raaviian/medical-image-processing.git",
    year: "2024"
  },
  {
    title: "PharmaApp",
    category: "Product Design",
    description: "Smart IoT medicine dispenser & scheduling app.",
    image: pharmaImg,
    link: "https://github.com/raaviian/pharmacyApp.git",
    year: "2023"
  },
  {
    title: "Liveness Check",
    category: "Biometric Security",
    description: "Facial Check-In verification with Amazon Rekognition.",
    image: livenessImg,
    link: "https://github.com/yourusername/liveness-checkin",
    year: "2024"
  },
  {
    title: "BizMatch AI",
    category: "B2B Platform",
    description: "Corporate matchmaking using vector embeddings.",
    image: businessImg,
    link: "https://github.com/yourusername/business-matching",
    year: "2023"
  },
  {
    title: "HeadCount",
    category: "Data Analytics & AI Label Detection",
    description: "Real-time foot traffic heatmaps & analysis.",
    image: crowdImg,
    link: "https://github.com/yourusername/crowd-management-dashboard",
    year: "2024"
  },
];

const Portfolio: React.FC = () => {
  const targetRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: targetRef,
  });

  // Transform vertical scroll to horizontal movement
  // Move from 0% to -65% (adjusted for more items)
  const x = useTransform(scrollYProgress, [0, 1], ["0%", "-85%"]);
  const progressScale = useTransform(scrollYProgress, [0, 1], ["0%", "100%"]);

  return (
    <section id="portfolio" ref={targetRef} className="relative h-[300vh] md:h-[400vh] bg-primary">
      <div className="sticky top-0 h-screen flex flex-col justify-center overflow-hidden">

        {/* Section Header (Fixed position visually) */}
        <div className="absolute top-20 md:top-24 left-4 md:left-24 z-10 mix-blend-difference pointer-events-none">
          <h2 className="text-xs md:text-sm font-mono text-accent-dim uppercase tracking-widest mb-1 md:mb-2">Web Based Projects</h2>
          <span className="font-serif text-white text-base md:text-xl">(Scroll for more)</span>
        </div>

        {/* Horizontal Track */}
        <motion.div style={{ x }} className="flex gap-6 md:gap-16 px-4 md:px-24 w-max items-center">
          {projects.map((project, i) => (
            <ProjectCard key={i} project={project} />
          ))}

          {/* End Card */}
          <div className="w-[40vw] md:w-[20vw] shrink-0 flex items-center justify-center">
            <a href="https://github.com/raaviian" target="_blank" rel="noopener noreferrer" className="font-serif text-2xl md:text-4xl text-white hover:text-zinc-500 underline transition-colors">
              View All Archived &rarr;
            </a>
          </div>
        </motion.div>

        {/* Progress Line */}
        <div className="absolute bottom-8 md:bottom-12 left-4 right-4 md:left-24 md:right-24 z-20">
          <div className="w-full h-[1px] bg-white/10 relative overflow-hidden">
            <motion.div
              style={{ width: progressScale }}
              className="absolute top-0 left-0 h-full bg-white"
            />
          </div>
        </div>
      </div>
    </section>
  );
};

const ProjectCard: React.FC<{ project: Project }> = ({ project }) => {
  return (
    <a
      href={project.link}
      target="_blank"
      rel="noopener noreferrer"
      className="group relative w-[85vw] md:w-[60vw] lg:w-[45vw] shrink-0"
    >
      {/* Image Container */}
      <div className="aspect-[4/3] md:aspect-[16/9] w-full overflow-hidden bg-secondary relative">
        <div className="absolute inset-0 bg-black/20 group-hover:bg-transparent transition-colors z-10" />
        <img
          src={project.image}
          alt={project.title}
          className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-700 transform group-hover:scale-105"
        />
      </div>

      {/* Content Below */}
      <div className="mt-4 md:mt-6 flex flex-col md:flex-row justify-between items-start md:items-end gap-3 md:gap-4">
        <div>
          <span className="font-mono text-xs text-accent-dim uppercase tracking-widest block mb-1">
            {project.category} — {project.year}
          </span>
          <h3 className="text-2xl md:text-4xl lg:text-5xl font-serif text-white group-hover:text-zinc-400 transition-colors">
            {project.title}
          </h3>
        </div>

        {/* Minimalism: Just a small 'View' indicator or short desc */}
        <div className="max-w-xs">
          <p className="text-xs md:text-sm text-gray-500 leading-relaxed line-clamp-2 md:line-clamp-none">
            {project.description}
          </p>
        </div>
      </div>
    </a>
  );
}

export default Portfolio;
